import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, FormattedDate, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_TIME,
  DATE_TYPE_DATETIME,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  propTypes,
} from '../../util/types';
import { subtractTime, isDST, isSameDay } from '../../util/dates';

import css from './OrderBreakdown.module.css';

const DSTMaybe = props => {
  const { startDate, endDate, isStart, dateType, timeZone } = props;
  const isDayTimeRange = dateType === DATE_TYPE_DATETIME;
  if (!isDayTimeRange) {
    return null;
  }

  const isStartInDST = isDST(startDate, timeZone);
  const isEndInDST = isDST(endDate, timeZone);
  const isDSTChanged = isStartInDST !== isEndInDST;
  const showDSTMsgForStart = isDSTChanged && isStart && isStartInDST;
  const showDSTMsgForEnd = isDSTChanged && !isStart && isEndInDST;

  return showDSTMsgForStart || showDSTMsgForEnd ? (
    <div className={css.itemLabel}>
      <FormattedMessage id="OrderBreakdown.bookingWithDSTInEffect" />
    </div>
  ) : null;
};

const getUSPrimaryTimeZoneLabel = timeZone => {
  if (!timeZone) return null;
  if (timeZone.includes('New_York')) return 'ET';
  if (timeZone.includes('Chicago')) return 'CT';
  if (timeZone.includes('Denver')) return 'MT';
  if (timeZone.includes('Los_Angeles')) return 'PT';
  return null;
};

const BookingPeriod = props => {
  const intl = useIntl();
  const {
    startDate,
    endDate,
    dateType,
    timeZone,
    userTimeZone: userTimeZoneMaybe,
    showLocalTimeNote: showLocalTimeNoteProp,
    timeAsPill = false,
  } = props;

  const timeZoneMaybe = timeZone ? { timeZone } : null;
  const resolvedUserTimeZone =
    userTimeZoneMaybe ||
    (typeof window !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null);

  const shouldShowLocalTimeNote =
    showLocalTimeNoteProp != null
      ? showLocalTimeNoteProp
      : !!resolvedUserTimeZone && !!timeZone && resolvedUserTimeZone !== timeZone;

  const localTimeRange = shouldShowLocalTimeNote
    ? dateType === DATE_TYPE_DATE
      ? intl.formatDateTimeRange(startDate, endDate, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: resolvedUserTimeZone,
        })
      : intl.formatDateTimeRange(startDate, endDate, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: resolvedUserTimeZone,
        })
    : null;

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? { weekday: 'long' }
      : {
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = { month: 'short', day: 'numeric' };

  // Booking timezone label for time ranges (ET/CT/MT/PT).
  const timeZoneShortLabel = dateType !== DATE_TYPE_DATE ? getUSPrimaryTimeZoneLabel(timeZone) : null;

  const dateLine = isSameDay(startDate, endDate, timeZone)
    ? intl.formatDate(startDate, { weekday: 'short', month: 'short', day: 'numeric', timeZone })
    : `${intl.formatDate(startDate, { weekday: 'short', month: 'short', day: 'numeric', timeZone })} - ${intl.formatDate(
        endDate,
        { weekday: 'short', month: 'short', day: 'numeric', timeZone }
      )}`;

  const startTimeLine = intl.formatDate(startDate, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  const endTimeLine = intl.formatDate(endDate, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

  const combinedTimeBlock = (
    <div className={css.bookingPeriodCombined}>
      <div className={css.bookingPeriodCombinedDate}>{dateLine}</div>
      <div
        className={classNames(css.bookingPeriodCombinedTime, {
          [css.bookingPeriodCombinedTimePill]: timeAsPill,
        })}
      >
        {startTimeLine} – {endTimeLine}
        {timeZoneShortLabel ? ` (${timeZoneShortLabel})` : ''}
      </div>
      <DSTMaybe
        startDate={startDate}
        endDate={endDate}
        isStart={true}
        dateType={dateType}
        timeZone={timeZone}
      />
      <DSTMaybe
        startDate={startDate}
        endDate={endDate}
        isStart={false}
        dateType={dateType}
        timeZone={timeZone}
      />
    </div>
  );

  return (
    <>
      {shouldShowLocalTimeNote && localTimeRange ? (
        <div className={css.localTimeNote}>
          <span className={css.localTimeNoteLabel}>
            <FormattedMessage id="OrderBreakdown.localTimeNoteLabel" />
          </span>
          <div className={css.localTimeNoteValue}>{localTimeRange}</div>
        </div>
      ) : null}

      {dateType === DATE_TYPE_DATE ? (
        <div className={css.bookingPeriod}>
          <div className={css.bookingPeriodSectionLeft}>
            <div className={css.dayLabel}>
              <FormattedMessage id="OrderBreakdown.bookingStart" />
            </div>
            <div className={css.dayInfo}>
              <span>
                <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} />
              </span>
            </div>
            <div className={css.itemLabel}>
              <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
            </div>
          </div>

          <div className={css.bookingPeriodSectionRight}>
            <div className={css.dayLabel}>
              <FormattedMessage id="OrderBreakdown.bookingEnd" />
            </div>
            <div className={css.dayInfo}>
              <span>
                <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
              </span>
            </div>
            <div className={css.itemLabel}>
              <FormattedDate value={endDate} {...dateFormatOptions} {...timeZoneMaybe} />
            </div>
          </div>
        </div>
      ) : (
        combinedTimeBlock
      )}
    </>
  );
};

/**
 * A line-item to show booking period for the OrderBreakdown
 *
 * @component
 * @param {Object} props
 * @param {propTypes.booking?} props.booking
 * @param {LINE_ITEM_NIGHT | LINE_ITEM_DAY | LINE_ITEM_HOUR} props.code
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {string} props.timeZone IANA time zone name
 * @returns {JSX.Element} line-item element for the order breakdown
 */
const LineItemBookingPeriod = props => {
  const { booking, code, dateType, timeZone } = props;

  if (!booking) {
    return null;
  }
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const showInclusiveEndDate = [LINE_ITEM_DAY].includes(code);
  const endDay = showInclusiveEndDate ? subtractTime(localEndDateRaw, 1, 'days') : localEndDateRaw;

  return (
    <>
      <div className={css.bookingLineItem}>
        <BookingPeriod
          startDate={localStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
          userTimeZone={props.userTimeZone}
          showLocalTimeNote={props.showLocalTimeNote}
          timeAsPill={props.timeAsPill}
        />
      </div>
    </>
  );
};

export default LineItemBookingPeriod;
